import React, { FC, useEffect, useRef, useState } from "react";
import parse from "html-react-parser";

import { DocumentForm } from "./DocumentForm";
import { getOptions, twigParser } from "./DocumentForm.helper";
import { FieldValues } from "react-hook-form";
import { DocumentSuccessResponse } from "../../hooks/DocumentSuccessResponse";
import "./WrapperForDocumentForm.css";
import { Box, Fade, Paper } from "@mui/material";
import documentStyles from "./WrapperForDocumentFormStyles";
import { Global } from "@emotion/react";
import { trackInteraction } from "../../api/interactionBuffer";

export type DocumentFormProps = {
  documentResponse: DocumentSuccessResponse;
  onFormDataSubmit: (formData: FieldValues) => void;
  visible: boolean;
};

export const WrapperForDocumentForm: FC<DocumentFormProps> = ({
  documentResponse,
  onFormDataSubmit,
  visible,
}) => {
  const { documentHTML, jsonData, id, title } = documentResponse;
  const [data, setData] = useState<any>(null);
  const [parsedData, setParsedData] = useState<
    JSX.Element | JSX.Element[] | null
  >(null);
  const [showComponent, setShowComponent] = useState(false);
  const paperRef = useRef<HTMLDivElement>(null);
  const scrollTrackedRef = useRef(false);

  useEffect(() => {
    setShowComponent(visible);
    if (visible) {
      trackInteraction("page_viewed", `document_${id}`);
      scrollTrackedRef.current = false;
    }
  }, [visible, id]);

  useEffect(() => {
    const handleScroll = () => {
      if (scrollTrackedRef.current) return;
      const scrollY = window.scrollY + window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;
      if (scrollY >= docHeight - 100) {
        scrollTrackedRef.current = true;
        trackInteraction("scrolled_to_bottom", `document_${id}`);
      }
    };

    if (visible) {
      window.addEventListener("scroll", handleScroll, { passive: true });
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, [visible, id]);

  useEffect(() => {
    const { html, widgetsProps } = twigParser(documentHTML || "", jsonData);

    const document = {
      id: id,
      html: html,
      widgets: widgetsProps,
      title: title,
    };
    setData(document);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!data) return;
    const options = getOptions(data.widgets);
    const parsedHTML = parse(data.html, options) as JSX.Element | JSX.Element[];
    setParsedData(parsedHTML);
  }, [data]);

  return (
    <>
      <Global styles={documentStyles} />
      <Fade in={showComponent} timeout={400}>
        <Box style={{ display: visible ? "block" : "none" }}>
          <Paper
            elevation={3}
            style={{
              padding: "20px",
            }}
          >
            {parsedData && (
              <DocumentForm
                parsedData={parsedData}
                onFormDataSubmit={onFormDataSubmit}
              />
            )}
          </Paper>
        </Box>
      </Fade>
    </>
  );
};
